import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from './icons';

interface RoleModalProps {
    role?: any | null;
    allPermissions: string[];
    onClose: () => void;
    onSave: (data: any, roleId: string | null) => Promise<void>;
}

const RoleModal: React.FC<RoleModalProps> = ({ role, allPermissions, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        permissions: [] as string[],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isEditing = !!role;

    useEffect(() => {
        if (isEditing) {
            setFormData({
                name: role.name,
                description: role.description,
                permissions: role.permissions || [],
            });
        }
    }, [role, isEditing]);

    const handlePermissionChange = (permission: string) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(permission)
                ? prev.permissions.filter(p => p !== permission)
                : [...prev.permissions, permission];
            return { ...prev, permissions: newPermissions };
        });
    };
    
    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData(prev => ({...prev, [e.target.name]: e.target.value}));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        try {
            await onSave(formData, role?.id || null);
            onClose();
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative mx-auto border w-full max-w-2xl shadow-lg rounded-md bg-white">
                <form onSubmit={handleSubmit}>
                    <div className="p-5 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-xl leading-6 font-bold text-gray-900 mb-4">{isEditing ? 'Edit Role' : 'Create New Role'}</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Role Name</label>
                                <input name="name" type="text" required value={formData.name} onChange={handleFormChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea name="description" required value={formData.description} onChange={handleFormChange} rows={2} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md" />
                            </div>
                            <div className="border-t pt-4">
                                <label className="block text-base font-medium text-gray-900">Permissions</label>
                                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-60 overflow-y-auto p-2 border rounded-md">
                                    {allPermissions.map(permission => (
                                        <div key={permission} className="flex items-start">
                                            <div className="flex items-center h-5">
                                                <input
                                                    id={permission}
                                                    name="permissions"
                                                    type="checkbox"
                                                    checked={formData.permissions.includes(permission)}
                                                    onChange={() => handlePermissionChange(permission)}
                                                    className="focus:ring-primary-light h-4 w-4 text-primary border-gray-300 rounded"
                                                />
                                            </div>
                                            <div className="ml-3 text-sm">
                                                <label htmlFor={permission} className="font-medium text-gray-700 capitalize">{permission.replace(/_/g, ' ')}</label>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {error && <p className="mt-3 text-sm text-red-600 p-2 bg-red-50 rounded">{error}</p>}
                    </div>
                    
                    <div className="items-center px-4 py-3 border-t flex flex-wrap justify-end gap-3 bg-gray-50 rounded-b-md">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 text-base font-medium rounded-md w-auto shadow-sm hover:bg-gray-300">
                            Cancel
                        </button>
                        <button type="submit" disabled={isLoading} className="px-4 py-2 bg-primary text-white text-base font-medium rounded-md w-auto shadow-sm hover:bg-primary-light disabled:bg-gray-400 flex items-center">
                            {isLoading && <LoadingSpinner className="w-5 h-5 mr-2" />}
                            {isEditing ? 'Save Changes' : 'Create Role'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RoleModal;